import { Stack, Inline, Text, vars } from '@etg/wings';
import { date } from '@eti/utils';
import { useDateFormat } from '@eti/providers';
import { useMemo } from 'react';
import { css, cx, mediaQueries } from '@eti/styles';

const wrapperCss = css`
  flex: 1 0 15%;
`;

const airportCityStyles = (isDifferentAirport: boolean) => css`
  color: ${isDifferentAirport ? vars.colors.signal.caution.dark : '#737373'};
  overflow: hidden;
  text-overflow: ellipsis;

  @media ${mediaQueries.large.up} {
    font-size: 0.875rem;
  }
`;

const timestampStyles = css`
  flex-wrap: nowrap;
  font-size: 1rem;
  font-weight: 700;
  line-height: 1;
  position: relative;
`;

const overnightIndicatorStyles = css`
  background: ${vars.colors.signal.information.tint};
  border-radius: 3px;
  color: ${vars.colors.signal.information.dark};
  font-size: 1rem;
  height: 20px;
  padding: 2px;
`;

const nearbyAirportStyles = css`
  background: ${vars.colors.signal.caution.tint};
  padding: 0 4px;
`;

interface TripDetailsLocationTimeAndDateProps {
  airportCode?: string;
  boundNumber: number;
  city?: string;
  className?: string;
  dateAndTime: string;
  durationInDays?: number;
  type: string;
  isDifferentAirport?: boolean;
}

const TripDetailsLocationTimeAndDate = ({
  airportCode,
  boundNumber,
  city,
  className,
  dateAndTime,
  durationInDays = 0,
  type,
  isDifferentAirport,
}: TripDetailsLocationTimeAndDateProps) => {
  const { time } = useDateFormat();
  const showAirportNameCode = useMemo(
    () => Boolean(airportCode) || Boolean(city),
    [airportCode, city],
  );

  return (
    <Stack align="center" className={wrapperCss} spacing={4}>
      <Inline
        alignY="center"
        className={cx(timestampStyles, className)}
        data-testid={`trip-bound-${boundNumber}-info-${type}Time`}
        spacing={4}
      >
        {date.format(dateAndTime, time)}
        {durationInDays > 0 && (
          <div
            className={overnightIndicatorStyles}
            data-testid={`trip-bound-${boundNumber}-info-overnight`}
          >
            {`+${durationInDays}`}
          </div>
        )}
      </Inline>
      {showAirportNameCode && (
        <Stack align="center" spacing={4}>
          <Inline
            className={isDifferentAirport ? nearbyAirportStyles : ''}
            data-testid={`trip-bound-${boundNumber}-info-${type}-location`}
            spacing={4}
          >
            <Text
              className={airportCityStyles(Boolean(isDifferentAirport))}
              data-testid={`trip-bound-${boundNumber}-info-${type}-airportCode`}
            >
              {airportCode}
            </Text>
            <Text
              className={airportCityStyles(Boolean(isDifferentAirport))}
              data-testid={`trip-bound-${boundNumber}-info-${type}-cityName`}
            >
              {city}
            </Text>
          </Inline>
        </Stack>
      )}
    </Stack>
  );
};

export { TripDetailsLocationTimeAndDate };
